<template>
  <el-dialog
    v-model="visibleModel"
    width="min(1100px, calc(100vw - 32px))"
    destroy-on-close
    append-to-body
    align-center
    class="tr-review-report-dialog"
    modal-class="tr-review-report-modal"
    :show-close="false"
  >
    <template #header>
      <div class="tr-report-head">
        <h2>TR 审核报告</h2>
        <div class="tr-report-head-actions">
          <slot name="actions" />
          <el-button text @click="visibleModel = false">关闭</el-button>
        </div>
      </div>
    </template>

    <div class="tr-report-body">
      <div class="tr-report-meta">
        <div class="tr-report-meta-top">
          <span class="tr-report-version">V1 最新</span>
          <span :class="verdictClass">{{ verdictLabel }}</span>
        </div>
        <p v-if="projectName">当前项目：{{ projectName }}</p>
      </div>

      <div class="tr-report-grid">
        <section class="tr-report-card">
          <h3>审核结果</h3>
          <div class="tr-report-card-scroll">
            <div class="tr-report-verdict-line" :class="verdictClass">审核建议：{{ verdictLabel }}</div>
            <p class="tr-report-reason">{{ reason || '暂无说明' }}</p>
            <div class="tr-report-summary">
              <div><b>审核依据</b><span>{{ ruleDocumentTitle || '未选择TP评审规则文档' }}</span></div>
              <div><b>Checklist</b><span>{{ checklistSummaryText }}</span></div>
              <div v-if="keyMissingSummary"><b>主要缺失</b><span>{{ keyMissingSummary }}</span></div>
            </div>
          </div>
        </section>
        <section class="tr-report-card">
          <h3>审核建议</h3>
          <div class="tr-report-card-scroll">
            <ul v-if="improvementSuggestions.length" class="tr-report-advice">
              <li v-for="(item, idx) in improvementSuggestions" :key="idx">{{ item }}</li>
            </ul>
            <p v-else class="tr-report-empty">暂无改进建议</p>
          </div>
        </section>
      </div>

      <section class="tr-report-card tr-report-checklist">
        <h3>交付物 Checklist 对比</h3>
        <div class="tr-report-card-scroll tr-report-card-scroll--checklist">
          <table class="tr-report-table">
            <colgroup>
              <col class="tr-report-col-deliverable" />
              <col class="tr-report-col-result" />
              <col class="tr-report-col-evidence" />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>标准交付项</th>
                <th>审核结果</th>
                <th>证据来源</th>
                <th>AI判断说明</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in checklistItems" :key="idx">
                <td>{{ row.deliverableName }}</td>
                <td><span :class="auditResultClass(row.auditResult)">{{ row.auditResult }}</span></td>
                <td>{{ row.evidenceSource || '—' }}</td>
                <td>{{ row.aiNote || row.matchedFileName || '—' }}</td>
              </tr>
              <tr v-if="!checklistItems.length">
                <td colspan="4" class="tr-report-empty">暂无 Checklist 对比数据</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <template v-if="approvalMode" #footer>
      <div class="tr-report-approval-foot">
        <el-button :loading="approving" @click="emit('reject')">审批不通过</el-button>
        <el-button type="primary" :loading="approving" @click="emit('approve')">审批通过</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { TrReviewChecklistItemResult, TrReviewChecklistSummary } from '@/api/projectApi';

const props = withDefaults(
  defineProps<{
    visible: boolean;
    projectName?: string;
    verdictLabel?: string;
    recommendPass?: boolean | null;
    reason?: string;
    ruleDocumentTitle?: string;
    keyMissingSummary?: string;
    checklistSummary?: TrReviewChecklistSummary | null;
    checklistItems?: TrReviewChecklistItemResult[] | null;
    improvementSuggestions?: string[] | null;
    approvalMode?: boolean;
    approving?: boolean;
  }>(),
  {
    approvalMode: false,
    approving: false,
  },
);

const emit = defineEmits<{
  'update:visible': [value: boolean];
  approve: [];
  reject: [];
}>();

const visibleModel = computed({
  get: () => props.visible,
  set: (v) => emit('update:visible', v),
});

const verdictLabel = computed(() => props.verdictLabel || (props.recommendPass ? '通过' : '不通过'));
const verdictClass = computed(() => (props.recommendPass ? 'is-pass' : 'is-fail'));
const checklistItems = computed(() => props.checklistItems ?? []);
const improvementSuggestions = computed(() => (props.improvementSuggestions ?? []).filter(Boolean));

const checklistSummaryText = computed(() => {
  const s = props.checklistSummary;
  if (!s) return '—';
  return `共 ${s.total ?? 0} 项，已提交 ${s.submitted ?? 0} 项，未具备 ${s.missing ?? 0} 项，无法判断 ${s.undetermined ?? 0} 项。`;
});

function auditResultClass(result?: string) {
  if (result === '已提交') return 'audit-pass';
  if (result === '未具备') return 'audit-fail';
  return 'audit-unknown';
}
</script>

<style scoped>
:global(.tr-review-report-modal) {
  cursor: default;
}

:global(.tr-review-report-dialog.el-dialog) {
  height: min(90vh, 900px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: default;
}

:global(.tr-review-report-dialog .el-dialog__header),
:global(.tr-review-report-dialog .el-dialog__footer) {
  flex-shrink: 0;
  cursor: default;
}

:global(.tr-review-report-dialog .el-dialog__body) {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: 8px;
  padding-bottom: 16px;
  cursor: default;
}

.tr-report-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 12px;
  min-height: 0;
  overflow: hidden;
}

.tr-report-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.tr-report-head h2 {
  margin: 0;
}
.tr-report-head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.tr-report-meta {
  flex-shrink: 0;
}
.tr-report-meta-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}
.tr-report-version {
  color: #1264e2;
  font-weight: 600;
}
.tr-report-meta p {
  margin: 0;
  color: #52647b;
}
.is-pass {
  color: #15803d;
  font-weight: 600;
}
.is-fail {
  color: #dc2626;
  font-weight: 600;
}
.tr-report-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  flex-shrink: 0;
  min-height: 0;
}
.tr-report-card {
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: 1px solid #dfe7f2;
  border-radius: 10px;
  background: #fff;
  padding: 14px;
}
.tr-report-card h3 {
  margin: 0 0 10px;
  font-size: 15px;
  flex-shrink: 0;
}
.tr-report-card-scroll {
  min-height: 0;
  max-height: 150px;
  overflow-y: auto;
  padding-right: 4px;
}
.tr-report-card-scroll--checklist {
  flex: 1;
  max-height: none;
  overflow: auto;
}
.tr-report-verdict-line {
  margin-bottom: 8px;
  font-weight: 600;
}
.tr-report-reason {
  margin: 0 0 10px;
  color: #52647b;
  line-height: 1.55;
}
.tr-report-summary {
  display: grid;
  gap: 6px;
  font-size: 14px;
}
.tr-report-summary b {
  margin-right: 8px;
}
.tr-report-summary span {
  color: #52647b;
}
.tr-report-advice {
  margin: 0;
  padding-left: 18px;
  color: #52647b;
  line-height: 1.55;
}
.tr-report-table {
  width: 100%;
  min-width: 800px;
  table-layout: fixed;
  border-collapse: collapse;
}
.tr-report-col-deliverable {
  width: 27%;
}
.tr-report-col-result {
  width: 110px;
}
.tr-report-col-evidence {
  width: 140px;
}
.tr-report-table th,
.tr-report-table td {
  border: 1px solid #dfe7f2;
  padding: 8px 10px;
  text-align: left;
  vertical-align: top;
  overflow-wrap: anywhere;
}
.tr-report-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f5f7fb;
  white-space: nowrap;
}
.tr-report-table td:nth-child(2) {
  white-space: nowrap;
}
.tr-report-checklist {
  flex: 1;
  min-height: 0;
}
.tr-report-empty {
  color: #728197;
  text-align: center;
}
.audit-pass {
  color: #15803d;
}
.audit-fail {
  color: #dc2626;
}
.audit-unknown {
  color: #d97706;
}
.tr-report-approval-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

@media (max-width: 768px) {
  .tr-report-grid {
    grid-template-columns: 1fr;
  }
  .tr-report-body {
    overflow-y: auto;
  }
  .tr-report-grid .tr-report-card-scroll {
    max-height: 100px;
  }
  .tr-report-checklist {
    flex: 0 0 min(50vh, 360px);
  }
}
</style>
